import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import { Sparkle, ChartLine, ChartBar, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * AdminAISpend — shows how much you're spending on AI this month.
 * Every AI call in Field Radar logs to db.ai_usage with rough per-model
 * pricing. This page rolls it up so you can price your subscription tiers
 * with real numbers instead of guessing.
 */
export default function AdminAISpend() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/ai-usage/summary", { params: { days }})
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load AI spend"))
      .finally(() => setLoading(false));
  }, [days]);

  const $ = (cents) => `$${(cents / 100).toFixed(2)}`;

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="ai-spend-page">
        <div className="flex items-center gap-3 flex-wrap">
          <Sparkle weight="fill" size={30} className="text-fuchsia-600"/>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight">AI Spend</h1>
          <div className="ml-auto flex gap-1">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)}
                data-testid={`ai-spend-range-${d}`}
                className={`h-9 px-3 font-black uppercase tracking-widest text-[10px] border-2 ${
                  days === d
                    ? "bg-slate-900 border-slate-900 text-white"
                    : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"
                }`}>{d}d</button>
            ))}
          </div>
        </div>
        <div className="mt-1 text-sm text-slate-600 flex items-start gap-2">
          <Info size={14} className="mt-0.5 text-slate-400"/>
          <span>Rough estimates using published per-image / per-token prices — real Emergent Universal Key bill may be lower. Use these numbers to price your subscription tiers.</span>
        </div>

        {loading && <div className="mt-8 text-slate-500">Loading…</div>}

        {!loading && data && (
          <>
            {/* Top row: KPIs */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="TOTAL SPEND" value={$(data.total_cost_cents)}
                testid="ai-spend-total" accent="bg-fuchsia-600 text-white"/>
              <Kpi label="AI CALLS" value={data.total_calls.toLocaleString()}
                testid="ai-spend-calls"/>
              <Kpi label={`AVG PER CALL`}
                value={data.total_calls > 0 ? `¢${(data.total_cost_cents / data.total_calls).toFixed(2)}` : "—"}
                testid="ai-spend-avg"/>
              <Kpi label={`PROJECTED / MO`}
                value={$(data.total_cost_cents * (30 / (data.days || 30)))}
                testid="ai-spend-projected" accent="bg-emerald-600 text-white"/>
            </div>

            {/* By endpoint */}
            <div className="mt-8">
              <div className="flex items-center gap-2 pb-1 border-b-2 border-slate-900">
                <ChartBar weight="fill" size={16}/>
                <div className="font-display font-black text-lg uppercase tracking-tight">Where the spend is going</div>
              </div>
              {data.by_endpoint.length === 0 ? (
                <div className="mt-6 p-8 text-center bg-slate-50 border-2 border-dashed border-slate-300">
                  <div className="text-slate-500">No AI activity yet in the last {days} days.</div>
                  <div className="mt-1 text-[11px] text-slate-400">Once you run a Nameplate Sweep or an Onboarding AI extract, spend will show here.</div>
                </div>
              ) : (
                <table className="mt-4 w-full text-sm" data-testid="ai-spend-endpoints">
                  <thead>
                    <tr className="text-left">
                      <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Endpoint</th>
                      <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Calls</th>
                      <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Cost</th>
                      <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_endpoint.map(row => {
                      const pct = data.total_cost_cents > 0 ? (row.cost_cents / data.total_cost_cents * 100) : 0;
                      return (
                        <tr key={row.endpoint} className="border-t border-slate-200">
                          <td className="py-2 font-mono text-xs">{row.endpoint}</td>
                          <td className="py-2 text-right tabular-nums">{row.calls.toLocaleString()}</td>
                          <td className="py-2 text-right tabular-nums font-black">{$(row.cost_cents)}</td>
                          <td className="py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-20 h-1.5 bg-slate-100 relative">
                                <div className="absolute inset-y-0 left-0 bg-fuchsia-500" style={{ width: `${pct}%` }}/>
                              </div>
                              <span className="tabular-nums text-[11px] text-slate-500 w-10 text-right">{pct.toFixed(0)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* By model */}
            {data.by_model.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-2 pb-1 border-b-2 border-slate-900">
                  <ChartLine weight="fill" size={16}/>
                  <div className="font-display font-black text-lg uppercase tracking-tight">By model</div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.by_model.map(m => (
                    <div key={m.model} className="p-3 border-2 border-slate-300 bg-white">
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">MODEL</div>
                      <div className="font-mono font-black text-sm truncate">{m.model}</div>
                      <div className="mt-2 flex items-baseline justify-between">
                        <div className="text-2xl font-display font-black">{$(m.cost_cents)}</div>
                        <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">{m.calls.toLocaleString()} calls</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing hint */}
            <div className="mt-8 p-4 bg-slate-900 text-white">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400">Pricing tip</div>
              <div className="mt-1 text-sm">
                At your projected <b>{$(data.total_cost_cents * (30 / (data.days || 30)))}</b>/month across the tenant, a fair breakeven baked into subscription is roughly <b>+${((data.total_cost_cents * (30 / (data.days || 30)) / 100) * 3).toFixed(2)}/tenant/month</b> (3× to cover margin + growth). Bake that into your Pro tier and quit worrying about metering.
              </div>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function Kpi({ label, value, testid, accent }) {
  return (
    <div className={`p-3 border-2 border-slate-900 ${accent || "bg-white"}`} data-testid={testid}>
      <div className={`text-[9px] font-black uppercase tracking-widest ${accent ? "opacity-80" : "text-slate-500"}`}>{label}</div>
      <div className="mt-1 font-display font-black text-2xl">{value}</div>
    </div>
  );
}
